// src/components/SiteBackdrop.js
//
// The site's background photo, filling the box it is placed in — the panel
// beside the sign-in form, which shows the first photo of the background
// slideshow (see SiteSlideshow.js for the home page).
//
// The photos are whatever an admin set in the dashboard, or the built-in farm
// photo. Which they are is only known once `site/photos` has been read, so a
// first visit shows a plain dark wash until then — never the built-in photo
// first and another one after it. A returning visitor's browser remembers the
// answer, and draws the photo at once.
import React from 'react';
import { Box } from '@mui/material';
import ProductPhoto from './ProductPhoto';
import useSiteData from './useSiteData';
import { sitePhoto } from '../photos';

/** The dark wash shown until the photo is known, and behind it while it loads. */
export const BACKDROP_COLOR = '#2c3220';

/** The first photo for a slot, or null while it is not yet known which that is. */
export function useSitePhoto(slot) {
  const data = useSiteData();
  return data ? sitePhoto(slot, data) : null;
}

/**
 * Sits behind everything else in its parent, which must be positioned and
 * isolated (`position: relative; isolation: isolate`) so the photo stays
 * beneath the parent's own wash and content.
 */
const SiteBackdrop = ({ slot = 'hero', sizes = '100vw', eager = false }) => {
  const photo = useSitePhoto(slot);
  return (
    <Box
      aria-hidden
      sx={{ position: 'absolute', inset: 0, zIndex: -1, backgroundColor: BACKDROP_COLOR }}
    >
      {photo && (
        <ProductPhoto
          photo={photo}
          sizes={sizes}
          eager={eager}
          priority={eager ? 'high' : undefined}
          sx={{ position: 'absolute', inset: 0, backgroundColor: 'transparent' }}
        />
      )}
    </Box>
  );
};

export default SiteBackdrop;
